import type { ReactNode } from "react";
import type { Categorization, FenceDetail } from "../../types/transaction";
import { formatAmount } from "../../utils/currency";
import { formatShortDate } from "../../utils/dates";

export interface FenceAnswers {
  /** Book the row to the clearing account ("Transfer between my accounts"). */
  onTransfer: () => void;
  /** Answer `not_a_transfer` naming one counterpart row by its transaction id. */
  onNotATransfer: (otherTransactionId: number) => void;
  /** Answer `refund`: a store or vendor refunded the card. */
  onRefund: () => void;
  /** Open the pick list to choose the row's category. */
  onChooseCategory: () => void;
}

interface FenceQuestionProps extends FenceAnswers {
  fence: FenceDetail;
  /** The listed rows, to name each counterpart the fence points at. */
  items: Categorization[];
  disabled: boolean;
}

// backend/prime/categorization/fence_record.py: the card's payment-or-refund verdict.
const POSITIVE_LIABILITY = "positive_liability";

/** The counterpart's own date, description and amount, when it is on the list. */
function counterpartName(transactionId: number, items: Categorization[]): string {
  const row = items.find((i) => i.transaction_id === transactionId);
  if (!row) return "";
  return `${formatShortDate(row.date)} ${row.description} ${formatAmount(row.amount)}`.trim();
}

/**
 * A held row's question (requirements What 3). The fence's sentence is the
 * row's reasoning, shown by the card; this renders the answers only, in the
 * words the requirements pin: on the card's payment-or-refund fence "I paid
 * this card from one of my accounts" and "A store or vendor refunded me";
 * on a transfer fence "Transfer between my accounts" and one "Not a transfer"
 * per counterpart, named; "Choose a category" on both.
 */
export function FenceQuestion({
  fence,
  items,
  disabled,
  onTransfer,
  onNotATransfer,
  onRefund,
  onChooseCategory,
}: FenceQuestionProps): ReactNode {
  const isCardCredit = fence.verdict === POSITIVE_LIABILITY;
  const counterparts = [...fence.business_matches, ...fence.held_matches];
  return (
    <div className="fence-question">
      <button type="button" disabled={disabled} onClick={onTransfer}>
        {isCardCredit ? "I paid this card from one of my accounts" : "Transfer between my accounts"}
      </button>
      {isCardCredit && (
        <button type="button" disabled={disabled} onClick={onRefund}>
          A store or vendor refunded me
        </button>
      )}
      {!isCardCredit &&
        counterparts.map((other) => (
          <div className="fence-counterpart" key={other}>
            <span>{counterpartName(other, items)}</span>
            <button type="button" disabled={disabled} onClick={() => onNotATransfer(other)}>
              Not a transfer
            </button>
          </div>
        ))}
      <button type="button" disabled={disabled} onClick={onChooseCategory}>
        Choose a category
      </button>
    </div>
  );
}
